import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EllipsisVerticalIcon, TruckIcon, PlusIcon } from '@heroicons/react/24/outline';
import AmbulanceVendorDocumentUploadModal from '../../../components/MasterAdmin/AmbulanceVendorDocumentUploadModal';
import { fetchAmbulanceProfile } from '../../../services/MasterAdmin/ambulanceService';
import { AmbulanceVendor, VendorDocument, VendorPolicy } from '../../../models/MasterAdmin/ambulanceBookings';
import { setVendorDocumentVerification, updateDocumentExpiryDate } from '../../../services/vendorDocumentsService';
import toast, { Toaster } from 'react-hot-toast';
import { useMasterAdminShellBranding } from '../../../contexts/MasterAdminShellBrandingContext';
import VendorPushNotificationLauncher from '../../../components/MasterAdmin/VendorPushNotificationLauncher';

const toSafeText = (value: unknown, fallback = 'N/A') => {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text.length ? text : fallback;
};

const formatDateTime = (value: unknown): string => {
  if (!value) return 'N/A';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

const normalizeDocumentStatus = (
  status: unknown,
  fallback: VendorDocument['status']
): VendorDocument['status'] => {
  if (status === 'pending' || status === 'approved' || status === 'rejected') {
    return status;
  }
  return fallback;
};

const parseStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item == null ? '' : String(item).trim()))
      .filter((item) => item.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parseStringArray(parsed);
      } catch {
        return [trimmed];
      }
    }
    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  return [];
};

const getDocumentCategory = (docType: string): 'Agency' | 'Ambulance' | 'Driver' | 'Other' => {
  const upper = (docType || '').toUpperCase();
  if (
    upper.includes('AADHAAR_CARD') ||
    upper.includes('PAN_CARD') ||
    upper.includes('BUSINESS_REGISTRATION')
  ) {
    if (upper.includes('DRIVER_AADHAAR') || upper.includes('DRIVER_DRIVER_AADHAAR')) {
      return 'Driver';
    }
    return 'Agency';
  }
  if (
    upper.includes('VEHICLE') ||
    upper.includes('RC') ||
    upper.includes('INSURANCE') ||
    upper.includes('FITNESS') ||
    upper.includes('PUC')
  ) {
    return 'Ambulance';
  }
  if (
    upper.includes('DRIVER') ||
    upper.includes('DL') ||
    upper.includes('LICENSE')
  ) {
    return 'Driver';
  }
  return 'Other';
};

const ValueRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{value ?? 'N/A'}</div>
  </div>
);

const Chips: React.FC<{ items: string[]; emptyLabel: string }> = ({ items, emptyLabel }) => {
  if (items.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span
          key={item}
          className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
        >
          {item}
        </span>
      ))}
    </div>
  );
};

const AmbulanceVendorDetailsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { setBranding } = useMasterAdminShellBranding();
  const [data, setData] = useState<AmbulanceVendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDocMenuId, setActiveDocMenuId] = useState<string | null>(null);
  const [documentActionLoadingId, setDocumentActionLoadingId] = useState<string | null>(null);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);

  // --- Expiry / Issue date edit modal ---
  const [expiryEditDoc, setExpiryEditDoc] = useState<VendorDocument | null>(null);
  const [expiryEditDate, setExpiryEditDate] = useState('');
  const [expiryEditIssued, setExpiryEditIssued] = useState('');
  const [expiryIsLifetime, setExpiryIsLifetime] = useState(false);
  const [expiryEditLoading, setExpiryEditLoading] = useState(false);

  const handleOpenExpiryEdit = (doc: VendorDocument) => {
    setExpiryEditDoc(doc);
    setExpiryEditDate(doc.expiryDate || '');
    setExpiryEditIssued(doc.issuedDate || '');
    setExpiryIsLifetime(doc.isLifetime ?? false);
    setActiveDocMenuId(null);
  };

  const handleSaveExpiry = async () => {
    if (!expiryEditDoc) return;
    try {
      setExpiryEditLoading(true);
      const updated = await updateDocumentExpiryDate(expiryEditDoc.documentId, {
        expiryDate: expiryIsLifetime ? null : (expiryEditDate || null),
        issuedDate: expiryEditIssued || null,
        isLifetime: expiryIsLifetime,
      });
      if (updated) {
        setData((prev) => {
          if (!prev) return prev;
          const docs = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
          return {
            ...prev,
            vendorDocuments: docs.map((d) =>
              d.documentId === expiryEditDoc.documentId
                ? {
                    ...d,
                    ...updated,
                    status: normalizeDocumentStatus(updated.status, d.status),
                  }
                : d
            ),
          };
        });
        toast.success('Document details updated.');
      }
      setExpiryEditDoc(null);
    } catch {
      toast.error('Failed to update document details.');
    } finally {
      setExpiryEditLoading(false);
    }
  };

  useEffect(() => {
    if (!vendorId) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAmbulanceProfile(vendorId);
        setData(res);
      } catch (e) {
        setError('Failed to load vendor details.');
        toast.error('Failed to load vendor details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vendorId]);

  useEffect(() => {
    if (data?.AmbulanceAgencyProfile) {
      const p = data.AmbulanceAgencyProfile;
      const pic = p.profilePic?.trim();
      setBranding({
        primary: toSafeText(p.agencyName, 'Ambulance vendor'),
        ...(pic ? { imageUrl: pic } : {}),
      });
    } else {
      setBranding(null);
    }
    return () => setBranding(null);
  }, [data, setBranding]);

  const vendorDocuments = useMemo<VendorDocument[]>(
    () => (Array.isArray(data?.vendorDocuments) ? (data?.vendorDocuments ?? []) : []),
    [data?.vendorDocuments]
  );

  const acceptedPolicies = useMemo<VendorPolicy[]>(
    () => (Array.isArray(data?.acceptedPolicies) ? (data?.acceptedPolicies ?? []) : []),
    [data?.acceptedPolicies]
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Vendor not found'}</p>
        <button
          onClick={() => navigate(`/vendors/ambulance/${vendorId}`)}
          className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
        >
          Back to vendor dashboard
        </button>
      </div>
    );
  }

  const profile = data.AmbulanceAgencyProfile;
  const ambulanceTypes = parseStringArray(profile.ambulanceTypes);
  const operationalAreas = parseStringArray(profile.operationalAreas);
  const profilePicUrl = profile.profilePic?.trim() ?? '';

  const handleViewDocument = (url?: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleVerifyToggle = async (doc: VendorDocument) => {
    try {
      setDocumentActionLoadingId(doc.documentId);
      const nextVerify = !doc.isVerified;
      const updatedDoc = await setVendorDocumentVerification(doc.documentId, nextVerify);
      if (!updatedDoc) return;

      setData((prev) => {
        if (!prev) return prev;
        const currentDocs = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
        return {
          ...prev,
          vendorDocuments: currentDocs.map((item) =>
            item.documentId === doc.documentId
              ? {
                  ...item,
                  ...updatedDoc,
                  status: normalizeDocumentStatus(updatedDoc.status, item.status),
                }
              : item
          ),
        };
      });
      toast.success(nextVerify ? 'Document verified successfully.' : 'Document unverified successfully.');
    } catch {
      setError('Failed to update document verification status.');
      toast.error('Failed to update document verification status.');
    } finally {
      setDocumentActionLoadingId(null);
      setActiveDocMenuId(null);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-6xl mx-auto p-3 sm:p-4 md:p-6">
      <Toaster position="top-right" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(`/vendors/ambulance/${vendorId}`)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {vendorId ? (
            <VendorPushNotificationLauncher
              vendorId={vendorId}
              vendorName={profile?.agencyName || undefined}
              targetAudience="SINGLE_AMBULANCE"
              compact
            />
          ) : null}
          <button
            type="button"
            onClick={() => navigate(`/monitoring?tab=intelligence&vendorId=${vendorId}&category=ambulance`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 active:scale-95 transition-all shadow-sm border border-blue-500/20"
          >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
            />
          </svg>
          Analyze ML Intelligence
        </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b dark:border-gray-700 flex items-center gap-3">
          {profilePicUrl ? (
            <img
              src={profilePicUrl}
              alt=""
              className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-600"
            />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30 ring-2 ring-gray-100 dark:ring-gray-600">
              <TruckIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">{profile.agencyName}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {toSafeText(profile.address)}, {toSafeText(profile.city)}, {toSafeText(profile.state)} - {toSafeText(profile.pinCode)}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                data.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : data.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
              </span>
              <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                data.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {data.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Basic Information */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Basic Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="Agency Name" value={toSafeText(profile.agencyName)} />
                <ValueRow label="Owner Name" value={toSafeText(profile.ownerName)} />
                <ValueRow label="Contact Number" value={toSafeText(profile.contactNumber)} />
                <ValueRow label="Email" value={toSafeText(profile.email)} />
                <ValueRow label="Number of Ambulances" value={toSafeText(profile.numOfAmbulances)} />
                <ValueRow label="Profile Created At" value={formatDateTime(profile.createdAt)} />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Address Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="Address" value={toSafeText(profile.address)} />
                <ValueRow label="Landmark" value={toSafeText(profile.landmark)} />
                <ValueRow label="City" value={toSafeText(profile.city)} />
                <ValueRow label="State" value={toSafeText(profile.state)} />
                <ValueRow label="Pincode" value={toSafeText(profile.pinCode)} />
                <ValueRow label="Precise Location" value={toSafeText(profile.preciseLocation)} />
              </div>
            </div>

            {/* Service Information */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Service Information
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Ambulance Types</label>
                  <div className="mt-1">
                    <Chips items={ambulanceTypes} emptyLabel="No ambulance types provided." />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Operational Areas</label>
                  <div className="mt-1">
                    <Chips items={operationalAreas} emptyLabel="No operational areas provided." />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Features</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {profile.gpsTrackingAvailable && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
                        GPS Tracking
                      </span>
                    )}
                    {profile.is24x7Available && (
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                        24/7 Service
                      </span>
                    )}
                    {!profile.gpsTrackingAvailable && !profile.is24x7Available && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">No feature flags available.</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Location Map</label>
                  <div className="mt-2 h-56 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                    <iframe
                      title="Ambulance agency location map"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(
                        (profile.preciseLocation && String(profile.preciseLocation).trim()) ||
                          `${profile.address || ''}, ${profile.city || ''}, ${profile.state || ''} ${profile.pinCode || ''}`
                      )}&z=15&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Documents and photos — uploaded files registered as vendor documents (incl. office photos) */}
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b dark:border-gray-700 pb-2">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                Documents and photos
              </h2>
              <button
                type="button"
                onClick={() => setIsDocumentUploadOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4" />
                Upload documents (PDF)
              </button>
            </div>
            {vendorDocuments.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No documents found.</p>
            ) : (() => {
              const agencyDocs = vendorDocuments.filter((d) => getDocumentCategory(d.documentType) === 'Agency');
              const ambulanceDocs = vendorDocuments.filter((d) => getDocumentCategory(d.documentType) === 'Ambulance');
              const driverDocs = vendorDocuments.filter((d) => getDocumentCategory(d.documentType) === 'Driver');
              const otherDocs = vendorDocuments.filter((d) => getDocumentCategory(d.documentType) === 'Other');

              // Align ambulance documents by vehicle (suffix index) wise
              const getVehicleIndex = (type: string) => {
                const match = type.match(/_(\d+)$/);
                return match ? parseInt(match[1], 10) : 1;
              };
              const docOrder = ['VEHICLE_RC', 'VEHICLE_INSURANCE', 'VEHICLE_FITNESS_CERTIFICATE', 'VEHICLE_PUC'];
              ambulanceDocs.sort((a, b) => {
                const idxA = getVehicleIndex(a.documentType);
                const idxB = getVehicleIndex(b.documentType);
                if (idxA !== idxB) return idxA - idxB;
                const baseA = a.documentType.replace(/_\d+$/, '');
                const baseB = b.documentType.replace(/_\d+$/, '');
                return docOrder.indexOf(baseA) - docOrder.indexOf(baseB);
              });

              const renderDocTable = (docs: typeof vendorDocuments, title: string, colorClass: string) => {
                if (docs.length === 0) return null;
                return (
                  <div className="space-y-3 bg-gray-50/50 dark:bg-gray-900/30 p-4 rounded-xl border border-gray-150 dark:border-gray-750">
                    <div className="flex items-center gap-2 border-b dark:border-gray-700 pb-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${colorClass}`}></span>
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                        {title} ({docs.length})
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                            <th className="py-2 pr-4 font-semibold">Type</th>
                            <th className="py-2 pr-4 font-semibold">Name</th>
                            <th className="py-2 pr-4 font-semibold">Number</th>
                            <th className="py-2 pr-4 font-semibold">Issued</th>
                            <th className="py-2 pr-4 font-semibold">Status</th>
                            <th className="py-2 pr-4 font-semibold">Expiry</th>
                            <th className="py-2 pr-4 font-semibold">Verified</th>
                            <th className="py-2 pr-4 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((doc) => (
                            <tr key={doc.documentId} className="border-b border-gray-100 dark:border-gray-800 transition-colors">
                              <td className="py-2.5 pr-4 font-mono text-xs text-gray-600 dark:text-gray-400">{doc.documentType}</td>
                              <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">{doc.documentName}</td>
                              <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-400">{doc.documentNumber || 'N/A'}</td>
                              <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-400">{doc.issuedDate || 'N/A'}</td>
                              <td className="py-2.5 pr-4">
                                <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  doc.status === 'approved'
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                    : doc.status === 'pending'
                                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                }`}>
                                  {doc.status}
                                </span>
                              </td>
                              <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-400">
                                {doc.isLifetime ? 'Lifetime' : doc.expiryDate || 'N/A'}
                              </td>
                              <td className="py-2.5 pr-4">
                                <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  doc.isVerified
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-850 dark:text-gray-300'
                                }`}>
                                  {doc.isVerified ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="py-2.5 pl-2 pr-1 text-right relative">
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center rounded-md p-1 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                                  onClick={() =>
                                    setActiveDocMenuId((prev) => (prev === doc.documentId ? null : doc.documentId))
                                  }
                                >
                                  <EllipsisVerticalIcon className="h-5 w-5" />
                                </button>
                                {activeDocMenuId === doc.documentId && (
                                  <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                    <button
                                      type="button"
                                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                      onClick={() => {
                                        handleViewDocument(doc.documentUrl);
                                        setActiveDocMenuId(null);
                                      }}
                                    >
                                      View
                                    </button>
                                    <button
                                      type="button"
                                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                      onClick={() => handleOpenExpiryEdit(doc)}
                                    >
                                      Edit Expiry / Issue
                                    </button>
                                    <button
                                      type="button"
                                      disabled={documentActionLoadingId === doc.documentId}
                                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-60 dark:hover:bg-gray-700"
                                      onClick={() => handleVerifyToggle(doc)}
                                    >
                                      {documentActionLoadingId === doc.documentId
                                        ? 'Updating...'
                                        : doc.isVerified
                                        ? 'Unverify'
                                        : 'Verify'}
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              };

              return (
                <div className="space-y-6">
                  {renderDocTable(agencyDocs, 'Agency Documents', 'bg-blue-500')}
                  {renderDocTable(ambulanceDocs, 'Ambulance Information', 'bg-emerald-500')}
                  {renderDocTable(driverDocs, 'Driver Documents', 'bg-amber-500')}
                  {renderDocTable(otherDocs, 'Other Documents', 'bg-gray-500')}
                </div>
              );
            })()}
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
              Accepted Policy
            </h2>
            {acceptedPolicies.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No accepted policies found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-4">Policy Type</th>
                      <th className="py-2 pr-4">Version</th>
                      <th className="py-2 pr-4">Accepted</th>
                      <th className="py-2 pr-4">Accepted Date</th>
                      <th className="py-2 pr-4">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acceptedPolicies.map((policy) => (
                      <tr key={policy.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-4">{policy.policyType}</td>
                        <td className="py-2 pr-4">{policy.version}</td>
                        <td className="py-2 pr-4">{policy.isAccepted ? 'Yes' : 'No'}</td>
                        <td className="py-2 pr-4">{formatDateTime(policy.acceptedAt)}</td>
                        <td className="py-2 pr-4">{policy.isActive ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <AmbulanceVendorDocumentUploadModal
        open={isDocumentUploadOpen}
        vendorId={vendorId || ''}
        onClose={() => setIsDocumentUploadOpen(false)}
        onUploaded={(doc) => {
          setData((prev) => {
            if (!prev) return prev;
            const currentDocs = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
            return { ...prev, vendorDocuments: [doc, ...currentDocs] };
          });
          toast.success('Document uploaded successfully.');
        }}
      />

      {/* Expiry Date Edit Modal */}
      {expiryEditDoc && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-in zoom-in-95 duration-200">
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Edit Document Validity</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {expiryEditDoc.documentType} — {expiryEditDoc.documentName}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Issued Date</label>
                <input
                  type="date"
                  value={expiryEditIssued}
                  onChange={(e) => setExpiryEditIssued(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={expiryEditDate}
                  onChange={(e) => setExpiryEditDate(e.target.value)}
                  disabled={expiryIsLifetime}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expiryIsLifetime}
                  onChange={(e) => {
                    setExpiryIsLifetime(e.target.checked);
                    if (e.target.checked) setExpiryEditDate('');
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Lifetime (no expiry)</span>
              </label>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setExpiryEditDoc(null)}
                disabled={expiryEditLoading}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveExpiry()}
                disabled={expiryEditLoading}
                className="flex-1 rounded-lg bg-indigo-600 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {expiryEditLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbulanceVendorDetailsPage;
